































































































































































































































































































































































































import Vue, { PropType } from "vue";
import { createNamespacedHelpers } from "vuex";
import moment from "moment-timezone";
import { Employee } from "@/types";

const { mapActions: appointmentActions } = createNamespacedHelpers(
  "CALENDAR_APPOINTMENTS"
);

interface Service {
  _id: string;
  name: string;
}

interface Client {
  _id: string;
  fullName: string;
}

interface Appointment {
  _id: string;
  startDate: string;
  status: string;
  paymentStatus: string;
  cost: number;
  client?: Client;
  employee?: Employee;
  services: Service[];
}

interface RoomData {
  room: {
    _id: string;
    roomName: string;
  };
  appointments: Appointment[];
  count: number;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export default Vue.extend<any, any, any, any>({
  name: "AppointmentRoomView",
  props: {
    employees: {
      type: Array as PropType<Employee[]>,
      default: () => [],
    },
    roomList: {
      type: Array,
      default: () => [],
    },
    startDate: {
      type: Object,
    },
    endDate: {
      type: Object,
    },
    status: {
      type: String,
    },
    paymentStatus: {
      type: String,
    },
    roomId: {
      type: String,
    },
  },
  data() {
    return {
      roomsData: [] as RoomData[],
      pagination: null as Pagination | null,
      currentPage: 1,
      loading: false,
      selectedStatus: null as string | null,
      selectedPaymentStatus: null as string | null,
      selectedEmployee: null as string | null,
      selectedRoom: null as string | null,
      specificStartDate: "",
      specificEndDate: "",
      statusOptions: ["All Appointments", "Pending", "Completed", "Cancelled"],
      paymentStatusOptions: [
        "All Payments",
        "Pending",
        "Completed",
        "Cancelled",
        "Refunded",
      ],
    };
  },
  computed: {
    totalAppointments() {
      return this.roomsData.reduce((sum, room) => sum + room.count, 0);
    },
    employeeList() {
      return this.employees;
    },
    // Enhanced filter computed properties
    hasActiveFilters() {
      return !!(
        this.selectedStatus ||
        this.selectedPaymentStatus ||
        this.selectedEmployee ||
        this.selectedRoom ||
        this.specificStartDate ||
        this.specificEndDate
      );
    },
    activeFilterCount() {
      let count = 0;
      if (this.selectedStatus) count++;
      if (this.selectedPaymentStatus) count++;
      if (this.selectedEmployee) count++;
      if (this.selectedRoom) count++;
      if (this.specificStartDate) count++;
      if (this.specificEndDate) count++;
      return count;
    },
  },
  watch: {
    status: {
      handler(newVal) {
        this.selectedStatus = newVal;
        this.fetchRoomData();
      },
      immediate: true,
    },
    paymentStatus: {
      handler(newVal) {
        this.selectedPaymentStatus = newVal;
        this.fetchRoomData();
      },
      immediate: true,
    },
    roomId: {
      handler(newVal) {
        this.selectedRoom = newVal;
        this.fetchRoomData();
      },
      immediate: true,
    },
    selectedEmployee: {
      handler(newVal) {
        console.log("Employee selection changed:", newVal);
        this.fetchRoomData();
      },
    },
    startDate: {
      handler() {
        this.fetchRoomData();
      },
      deep: true,
    },
    endDate: {
      handler() {
        this.fetchRoomData();
      },
      deep: true,
    },
  },
  mounted() {
    this.initializeDates();
    this.fetchRoomData();
  },
  methods: {
    ...appointmentActions(["fetchAppointmentsGroupedByRoom"]),

    initializeDates() {
      if (this.startDate) {
        this.specificStartDate = moment(this.startDate).format("YYYY-MM-DD");
      }
      if (this.endDate) {
        this.specificEndDate = moment(this.endDate).format("YYYY-MM-DD");
      }
    },

    async fetchRoomData() {
      this.loading = true;
      try {
        const businessId =
          this.$store.getters.role?.business?._id ||
          localStorage.getItem("businessId");
        let params = `?businessId=${businessId}`;

        // Add filters
        if (this.selectedStatus && this.selectedStatus !== "All Appointments") {
          params += `&status=${this.selectedStatus.toLowerCase()}`;
        }

        if (
          this.selectedPaymentStatus &&
          this.selectedPaymentStatus !== "All Payments"
        ) {
          params += `&paymentStatus=${this.selectedPaymentStatus.toLowerCase()}`;
        }

        if (this.selectedEmployee) {
          params += `&employeeId=${this.selectedEmployee}`;
          console.log("Employee filter applied:", this.selectedEmployee);
        }

        if (this.selectedRoom) {
          params += `&roomId=${this.selectedRoom}`;
        }

        if (this.specificStartDate) {
          params += `&startDate=${moment(this.specificStartDate)
            .startOf("day")
            .toISOString()}`;
        } else if (this.startDate) {
          params += `&startDate=${moment(this.startDate).toISOString()}`;
        }

        if (this.specificEndDate) {
          params += `&endDate=${moment(this.specificEndDate)
            .endOf("day")
            .toISOString()}`;
        } else if (this.endDate) {
          params += `&endDate=${moment(this.endDate).toISOString()}`;
        }

        params += `&page=${this.currentPage}&limit=10`;

        console.log("API params:", params);
        const response = await this.fetchAppointmentsGroupedByRoom(params);
        if (response) {
          this.roomsData = response.rooms || [];
          this.pagination = response.pagination;
        }
      } catch (error) {
        console.error("Error fetching room data:", error);
      } finally {
        this.loading = false;
      }
    },

    onFilterChange() {
      this.currentPage = 1;
      this.fetchRoomData();
    },

    onPageChange(page: number) {
      this.currentPage = page;
      this.fetchRoomData();
    },

    formatTime(dateString: string): string {
      return moment(dateString).format("HH:mm");
    },

    formatDate(dateString: string): string {
      return moment(dateString).format("DD/MM/YYYY");
    },

    getStatusColor(status: string): string {
      switch (status?.toLowerCase()) {
        case "completed":
          return "success";
        case "pending":
          return "warning";
        case "cancelled":
          return "error";
        default:
          return "grey";
      }
    },

    getPaymentStatusColor(paymentStatus: string): string {
      switch (paymentStatus?.toLowerCase()) {
        case "completed":
          return "success";
        case "pending":
          return "warning";
        case "cancelled":
        case "refunded":
          return "error";
        default:
          return "grey";
      }
    },

    getRoomStatusColor(count: number): string {
      if (count >= 10) return "success";
      if (count >= 5) return "warning";
      if (count > 0) return "info";
      return "grey";
    },

    viewAppointment(appointment: Appointment) {
      this.$emit("view-appointment", appointment);
    },

    editAppointment(appointment: Appointment) {
      this.$emit("edit-appointment", appointment);
    },

    goBackToCalendar() {
      this.$emit("back-to-calendar");
    },

    exportCsv() {
      const appointments = this.roomsData.flatMap((room) =>
        room.appointments.map((appointment) => ({
          ...appointment,
          roomName: room.room?.roomName || "Unassigned",
        }))
      );

      const headers =
        "Room,Client,Employee,Services,Date,Time,Status,Payment Status,Cost";
      const rows = appointments.map((appointment) => {
        const services =
          appointment.services?.map((s: Service) => s.name).join(";") || "";
        const client = appointment.client?.fullName || "";
        const employee = appointment.employee?.fullName || "";
        const date = moment(appointment.startDate).format("D/MM/YYYY");
        const time = moment(appointment.startDate).format("HH:mm");

        return `${
          appointment.roomName
        },${client},${employee},${services},${date},${time},${
          appointment.status
        },${appointment.paymentStatus},${appointment.cost || 0}`;
      });

      const csvContent = [headers, ...rows].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute("download", "room-appointments.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
});
